'use client'

import { useMemo, useRef, useState } from 'react'
import { Search, XIcon } from 'lucide-react'

import { RevealP, RevealSection } from '@/components/RevealSection'
import TeamMemberCard from '@/components/TeamMemberCard'
import MemberModal from '@/components/TeamMemberCard/components/MemberModal'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { useLenisContext } from '@/providers/Lenis'

import type { Endorsed, LeadershipBlock as LeadershipBlockProps, TeamMember } from '@/payload-types'

const ITEMS_PER_PAGE = 9

type TeamMemberRelationItem = NonNullable<LeadershipBlockProps['teamMembers']>[number]

function getMemberDoc(item: TeamMemberRelationItem): TeamMember | Endorsed | null {
  const v = item.value
  return typeof v === 'number' ? null : v
}

/**
 * Renders a searchable, paginated grid of team member cards with a detail modal.
 * Search and pagination are computed client-side from the populated relationship data.
 */
export const LeadershipBlock: React.FC<LeadershipBlockProps> = ({
  heading,
  subheading,
  teamMembers,
}) => {
  const members = useMemo(() => {
    return (
      teamMembers
        ?.map((member) => getMemberDoc(member))
        .filter((member): member is TeamMember | Endorsed => member !== null) ?? []
    )
  }, [teamMembers])

  const [activeMember, setActiveMember] = useState<TeamMember | Endorsed | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const blockRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { lenis } = useLenisContext()

  const normalizedSearch = searchTerm.trim().toLowerCase()
  const filteredMembers = normalizedSearch
    ? members.filter((member) => member.name.toLowerCase().includes(normalizedSearch))
    : members
  const pageMembers = filteredMembers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  )

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
    lenis?.scrollTo(blockRef.current as HTMLElement, { offset: -84 })
    gridRef.current?.focus()
  }

  const handleMemberClick = (member: TeamMember | Endorsed) => {
    setActiveMember(member)
    setIsDialogOpen(true)
  }

  if (!members.length) return null

  return (
    <div ref={blockRef} className="wrapper">
      <RevealSection
        stagger
        threshold={0.005}
        className="flex flex-col gap-4 pb-16 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
      >
        <div>
          <h2 className="font-family-display text-3xl leading-10 font-normal text-foreground md:text-4xl">
            {heading}
          </h2>
          {subheading && (
            <p className="font-family-body text-2xl text-muted-foreground md:text-3xl">
              {subheading}
            </p>
          )}
        </div>
        <div className="w-full sm:w-auto">
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Search by name"
            IconComponent={Search}
            className="sm:min-w-75"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onClear={() => handleSearchChange('')}
            autoComplete="none"
            aria-label="Search team members"
          />
        </div>
      </RevealSection>
      {filteredMembers.length > 0 ? (
        <>
          <RevealSection
            key={`${normalizedSearch}-${currentPage}`}
            stagger
            threshold={0.001}
            ref={gridRef as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {pageMembers.map((member) => (
              <TeamMemberCard key={member.id} member={member} onClick={handleMemberClick} />
            ))}
          </RevealSection>
          <RevealSection>
            <Pagination
              numResults={filteredMembers.length}
              resultsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              className="my-12"
            />
          </RevealSection>
        </>
      ) : (
        <div className="py-12 text-center font-body text-card-foreground">
          <RevealP className="font-body text-lg font-medium">
            No team members found {searchTerm.trim() ? `matching "${searchTerm}"` : ''}
          </RevealP>
        </div>
      )}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl border-none bg-transparent p-0" customCloseButton>
          <DialogDescription className="sr-only">
            Information about {activeMember?.name}
          </DialogDescription>
          <DialogClose asChild>
            <Button
              variant="icon"
              customIcon={<XIcon className="size-4" />}
              className="absolute top-2 right-6 z-10"
            />
          </DialogClose>
          <DialogTitle className="sr-only"> {activeMember?.name} </DialogTitle>
          <div className="relative wrapper">
            <MemberModal member={activeMember as TeamMember} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
